import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { splitBaseSceneNote } from './base-scene-note';

import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';
import { computeSceneGroupShortIds, resolveSceneGroupHandle } from './scene-group-addressing';
import { resolveSplitBoundaryOperation } from './split-placement';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { isSceneBlurred, markScenesBusy } from 'js/src/data/agent-activity';
import { checkpointVideo } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary, splitSceneGroupOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const splitSceneGroupSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the voiceover or talking-head scene group to split (from the video state).',
        ),
    splits: z
        .array(
            z.object({
                words: z
                    .array(z.string())
                    .min(1)
                    .describe(
                        'Consecutive tokens from the transcript; the scene group is split immediately after the last of these. Provide enough tokens to uniquely identify the location — they must match exactly, in order, as spelled in the transcript, including any `<pause seconds="N"/>` token that falls between them.',
                    ),
            }),
        )
        .min(1)
        .describe(
            'Every point to split this scene group at, in any order. N points make N+1 scene groups; split at every boundary in one call rather than one call per split.',
        ),
});
export type SplitSceneGroupArgs = z.infer<typeof splitSceneGroupSchema>;

interface SplitPoint {
    words: string[];
    splitMillis: number;
}

function describeSplit(index: number, words: string[], total: number): string {
    const quoted = `"${words.join(' ')}"`;
    return total === 1 ? `Splitting after ${quoted}` : `splits[${index}] (after ${quoted})`;
}

export class SplitSceneGroupTool extends VideoStateTool<typeof splitSceneGroupSchema> {
    protected override readonly schema = splitSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.splitSceneGroup;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Split a voiceover or talking-head scene group at one or more points in its transcript. Each split happens immediately AFTER its sequence of transcript words: the original scene group keeps everything up to the first split point, and each following section becomes a new scene group, in order, right after it. The words must appear consecutively in the transcript and cannot be the very last word (there must be at least one word after each split point). Choose ${TOOL_NAMES.splitSceneGroup} when the user wants a structural break in the narration, you need a silent scene or non-speaking segment between spoken parts, or parts of the transcript need to become independently editable sections. (To keep only one section of the recording and discard the rest, use ${TOOL_NAMES.trimSceneGroup}. To emphasize specific words without changing the audio structure, use ${TOOL_NAMES.addVoiceoverScene}.)`;

    public override getToolDisplayName(): string {
        return 'Splitting scene group';
    }

    public get statusIcon(): string {
        return 'call_split';
    }

    public async execute(args: SplitSceneGroupArgs, context: ToolExecutionContext) {
        const { parentActionGroup } = context;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Only voicedriven scene groups (voiceover / talking head) have a transcript to split on.
        // originalTranscript is eagerly kept in sync with the lexical editor (LuminarySyncPlugin),
        // so its word times reflect any edits the user has made.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);
        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups can be split.`;
        }

        const { resolveSplitBoundary, splitSceneGroup } = makeOperationsCallable(
            { resolveSplitBoundaryOperation, splitSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        // Every point is resolved before anything changes, so a bad one leaves the group untouched.
        const points: SplitPoint[] = [];
        for (const [index, { words }] of args.splits.entries()) {
            const boundary = resolveSplitBoundary(sceneGroup, words);
            if ('error' in boundary) {
                return `Error: ${describeSplit(index, words, args.splits.length)}: ${boundary.error} Nothing was split.`;
            }
            const duplicate = points.find((point) => point.splitMillis === boundary.splitMillis);
            if (duplicate) {
                return `Error: ${describeSplit(index, words, args.splits.length)} is the same point as "${duplicate.words.join(' ')}". Nothing was split.`;
            }
            points.push({ words, splitMillis: boundary.splitMillis });
        }
        points.sort((a, b) => a.splitMillis - b.splitMillis);

        const sceneIdsBeforeSplit = this.snapshotSceneIds();
        const stateBeforeSplit = this.getState();
        const splittingLoadingGroup = sceneGroup.scenes.some(
            (scene) =>
                !isSceneAnOverlay(stateBeforeSplit, scene) &&
                isSceneBlurred(stateBeforeSplit, scene.id),
        );

        try {
            // Latest point first: each split leaves the original group holding everything before it,
            // so the earlier points still resolve in it, and each new group lands in order after it.
            const failed: SplitPoint[] = [];
            for (const point of [...points].reverse()) {
                const groupIdsBeforeSplit = new Set(
                    this.luminary.sceneGroups.map((group) => group.id),
                );
                splitSceneGroup(sceneGroup.id, point.splitMillis, false, parentActionGroup);
                const followingGroup = this.luminary.sceneGroups[sceneGroup.index + 1];
                // A real split inserts a fresh group right after the target. If none appeared the
                // split was a no-op: the point is at or after the end of what this group still holds.
                if (!followingGroup || groupIdsBeforeSplit.has(followingGroup.id)) {
                    failed.push(point);
                } else {
                    context.subagentScope?.claimSceneGroup(followingGroup.id);
                }
            }

            const groupShortIds = computeSceneGroupShortIds(
                this.luminary.sceneGroups.map((group) => group.id),
            );
            const originalShortId = groupShortIds.get(sceneGroup.id) ?? sceneGroup.id;
            const quote = (point: SplitPoint) => `"${point.words.join(' ')}"`;

            if (failed.length === points.length) {
                const followingGroup = this.luminary.sceneGroups[sceneGroup.index + 1];
                const retargetHint =
                    followingGroup && points.length === 1
                        ? ` If you already split this group, the words after ${quote(points[0])} now belong to the following scene group (${groupShortIds.get(followingGroup.id) ?? followingGroup.id}) — split that group instead.`
                        : '';
                return `Error: Splitting scene group ${originalShortId} after ${points.map(quote).join(', ')} had no effect — there is nothing after ${points.length === 1 ? 'that point' : 'those points'} in this scene group to move into a new group.${retargetHint}`;
            }

            // The new groups' base scenes are copies of one still loading, so they are too.
            if (splittingLoadingGroup) {
                const stateAfterSplit = this.getState();
                markScenesBusy(
                    this.dispatch,
                    this.luminary.scenes
                        .filter(
                            (scene) =>
                                !sceneIdsBeforeSplit.has(scene.id) &&
                                !isSceneAnOverlay(stateAfterSplit, scene),
                        )
                        .map((scene) => scene.id),
                );
            }

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const applied = points.filter((point) => !failed.includes(point));
            const resultingGroups = this.luminary.sceneGroups.slice(
                sceneGroup.index,
                sceneGroup.index + applied.length + 1,
            );
            const sections = resultingGroups
                .map((group, index) => {
                    const shortId = groupShortIds.get(group.id) ?? group.id;
                    const extent =
                        index < applied.length
                            ? `up to and including ${quote(applied[index])}`
                            : 'the rest';
                    return `- ${shortId}: ${extent}`;
                })
                .join('\n');
            const failedNote =
                failed.length > 0
                    ? `\nThese points had no effect, since nothing after them is left in this scene group: ${failed.map(quote).join(', ')}.`
                    : '';

            // A split duplicates the group and cuts each part, so each new group carries copies of
            // whatever the original held — base scenes only if some stretch was uncovered.
            const state = this.getState();
            const hasBaseScene = resultingGroups
                .slice(1)
                .some((group) => group.scenes.some((scene) => !isSceneAnOverlay(state, scene)));

            return `Success: Split scene group ${originalShortId} into ${resultingGroups.length} scene groups, in order:\n${sections}${failedNote}${hasBaseScene ? splitBaseSceneNote(resultingGroups.length - 1) : ''}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error splitting scene group: ${errorMessage}`;
        }
    }
}
